import React, { ReactNode, useEffect, useRef, useState } from "react";
import {
  Table,
  Button,
  Space,
  Switch,
  Typography,
  Popover,
  TableColumnsType,
  Popconfirm,
  Tabs,
  Modal,
  Form,
  Input,
  message,
  Pagination,
  Spin,
  Row,
  Col,
  Select,
  Radio,
  Checkbox,
  Alert,
} from "antd";
import { useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react";
import { ContentWrapper } from "../../Components/Layout/ContentWrapper";
import { DataTable } from "../../Components/DataTable/DataTable";
import { panelApiUrl } from "~/Utils";
import axios from "axios";
import { api } from "../../../SPA/Services/api.service";
import { Formik, Field, FieldProps, ErrorMessage } from "formik";
import TextArea from "antd/es/input/TextArea";
import * as yup from "yup";
import { debounce, isBoolean, startCase, toLower } from "lodash";
import { SuccessModal } from "@components/SuccessModal/SuccessModal";
import { getFormattedDate, getKey, switchHasRole } from "@utils/CommonUtils";
import { wasUpdated } from "../../Utils/auditFields";
import { apiSortField } from "../../Utils/sortField";
import { useLocalStorage } from "@hooks/useLocalStorage";
import { useQuery } from "../../Hooks/useQuery";
import { useNotification } from "../../Hooks/useNotification";
import pillStyles from "../WorkOrder/WorkOrder.module.scss";

const initialValue = {
  rejectionReason: "",
};

interface FormItems {
  division: string;
  Admin: boolean;
  Approver: boolean;
  userName: string;
  userEmail: string;
  role: string | null;
}

const defaultValue = {
  division: "",
  Admin: false,
  Approver: false,
  userName: "",
  userEmail: "",
  role: "",
} as FormItems;

interface RoleFormValues {
  role: string[];
}

export const PanelUserManagement = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<any>([]);
  const [modal, setModal] = useState(false);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [recordsPerPage, setRecordsPerPage] = useState<number>(10);
  const [isLoading, setIsLoading] = useState(false);
  const [switchLoading, setSwitchLoading] = useState(false);
  const [buttonLoading, setButtonLoading] = useState(false);
  const [commentLoading, setCommentLoading] = useState(false);
  const [rejectRecord, setRejectRecord] = useState<any>();
  const [refetch, setRefetch] = useState(false);
  const [page, setPage] = useState<number>(0);
  const [sortOrder, setSortOrder] = useState("desc");
  /* The list endpoints sort on createdOn — see Utils/sortField. */
  const [orderByName, setOrderByName] = useState("createdOn");
  const [pageStart, setPageStart] = useState<number>(0);
  const [selectedRowId, setSelectedRowId] = useState<string | null>(null);
  const [clearSearch, setClearSearch] = useState(false);
  const [apiUrl, setApiUrl] = useState<any>("");
  const [searchString, setSearchString] = useState("");
  const [loading, setLoading] = useState(false);
  const [successLoader, setSuccessLoader] = useState(false);
  const [defUser, setDefUser] = useLocalStorage("user", null);
  const [divisionModal, setDivisionModal] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<any>();
  const [modalRecord, setModalRecord] = useState<any>(null);
  const [modalValue, setModalValue] = useState<string>("");
  const tableRef = useRef<any>(null);
  const query = useQuery();
  const { openNotification } = useNotification();

  const validationSchema1 = yup.object().shape({
    division: yup.string().strict(true).required("Division is required"),
  });

  const validationSchemaModal = yup.object().shape({
    // role: yup.string().nullable().required("Role is required"),
  });

  const validationSchema = yup.object().shape({
    rejectionReason: yup.string().required("Reject Reason is required field"),
  });

  useEffect(() => {
    if (!query.get("activeTab")) {
      navigate(`/workorder-user-management?activeTab=activeUsers`, {
        replace: true,
      });
    }
  }, []);

  // Scroll to table on page change
  useEffect(() => {
    if (tableRef.current) {
      tableRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [page]);

  // Debounced search
  const handleSearchChange = debounce((value: string, isLoading: boolean) => {
    setClearSearch(false);
    setSearchString(value);
    setIsLoading(isLoading);
  }, 1000);

  // Fetch records from API
  const getRecords = (sortOrder: string, orderByParam: string, searchString: any, currentPage?: number) => {
    setIsLoading(true);
    let url = `${panelApiUrl}/user/get-user-list`;

    if (query.get("activeTab") === "activeUsers") {
      url = `${panelApiUrl}/user/get-user-list?isActive=true&page=${currentPage || 0}&size=${recordsPerPage}&order=${sortOrder}&orderBy=${apiSortField(orderByParam)}&searchText=${encodeURIComponent(
        searchString
      )}`;
    } else if (query.get("activeTab") === "deactivatedUsers") {
      url = `${panelApiUrl}/user/get-user-list?isActive=false&page=${currentPage || 0}&size=${recordsPerPage}&order=${sortOrder}&orderBy=${apiSortField(orderByParam)}&searchText=${encodeURIComponent(
        searchString
      )}`;
    }
    setApiUrl(url);
    api
      .get(url)
      .then((res: any) => {
        if (res && res.data) {
          setTotalRecords(res.data.response?.data?.totalCount || 0);
          let users = res?.data?.response?.data?.records;
          setData(Array.isArray(users) ? users : []);
        }
        setIsLoading(false);
      })
      .catch((err) => {
        setData([]);
        message.error("Error fetching users");
        setIsLoading(false);
      });
  };

  const ROLE_MAP: Record<string, string> = {
    PANEL_ADMIN: "Admin",
    PANEL_CREATOR: "Creator",
    PANEL_REVIEWER: "Approver",
    PANEL_MASTER_REVIEWER: "Master Approver",
  };

  // Handle table sorting
  const handleTableSort = (column: any, sortOrder: string, page: any) => {
    let order = "";
    if (column.sorter) {
      if (sortOrder === "asc") {
        setSortOrder("desc");
        order = "desc";
      } else if (sortOrder === "desc") {
        setSortOrder("asc");
        order = "asc";
      }
    }
    setOrderByName(column.dataIndex);
    getRecords(order, column.dataIndex, searchString, pageStart);
  };

  /**
   * Wording for what `PUT /user/update-roles` reports.
   *
   * Clearing a user's last role has the API answer "User deleted successfully
   * as no roles were assigned!" — but nothing is deleted, the user stays in
   * the list with no roles, and reading "deleted" in a toast after unticking a
   * role is alarming. This route never deletes anything, so "removed" is what
   * the toast says.
   */
  const describeRoleUpdate = (message?: string) =>
    String(message ?? "").replace(/\bdeleted\b/gi, "removed");

  // Update user role
  const updateRole = (record?: any, role?: any, isActive?: boolean) => {
    if (isSelf(record)) {
      openNotification({
        title: "Not allowed",
        message: "User cannot edit their roles themselves.",
        type: "error",
      });
      return;
    }
    setSwitchLoading(true);
    const payload = {
      uniqueId: record?.uniqueId,
      newRoles: isActive ? role : [],
    };

    api
      .put(`${panelApiUrl}/user/update-roles`, payload)
      .then((res: any) => {
        openNotification({
          title: "Updated user role",
          message: describeRoleUpdate(res?.data?.message),
          type: "success",
        });
        setRefetch((prevState: boolean) => !prevState);
        setSwitchLoading(false);
        setSelectedRowId(null);
        setLoading(false);
      })
      .catch((err: any) => {
        openNotification({
          title: "Failed to update role",
          message: err?.data?.message,
          type: "error",
        });
        setSwitchLoading(false);
        setLoading(false);
      });
  };

  const updateActiveRole = (record: any, activate: boolean) => {
    setButtonLoading(true);
    const formData = new FormData();
    formData.append("uniqueId", record?.uniqueId);

    const endpoint = activate ? `${panelApiUrl}/user/user-activate` : `${panelApiUrl}/user/user-de-activate`;

    api
      .post(endpoint, formData)
      .then((res: any) => {
        setButtonLoading(false);
        setRefetch((prevState: boolean) => !prevState);
      })
      .catch((err: any) => {
        setButtonLoading(false);
        message.error("Error updating user status");
      });
  };

  /**
   * Is this row the signed-in user?
   *
   * A user may not change their own role or deactivate their own account. The
   * server refuses both, but the refusal surfaced as a bare "Failed to update
   * role", and the deactivate action was offered as though it would work.
   * Matched on e-mail, which is the identity both stores agree on.
   */
  const signedInEmail = (() => {
    const read = (key: string) => {
      try {
        return JSON.parse(localStorage.getItem(key) || "{}");
      } catch {
        return {};
      }
    };
    const panelUser = read("panelUser");
    const user = read("user");
    return String(panelUser?.email || panelUser?.emailAddress || user?.email || "")
      .trim()
      .toLowerCase();
  })();

  const isSelf = (record: any) =>
    !!signedInEmail &&
    String(record?.emailAddress || record?.userEmail || "").trim().toLowerCase() ===
      signedInEmail;

  const handleChangeRole = (record: any, value: any) => {
    if (isSelf(record)) {
      openNotification({
        title: "Not allowed",
        message: "User cannot edit their roles themselves.",
        type: "error",
      });
      return;
    }

    const activeRole = Array.isArray(record.roles)
      ? record.roles.find((role: string) => role === "PANEL_ADMIN" || role === "PANEL_REVIEWER")
      : record.currentRole;

    setSelectedRowId(record.uniqueId);
    setModalRecord(record);
    setModalValue(activeRole || record.roles || "");
  };

  const handleModalClose = () => {
    setSelectedRowId(null);
    setModalValue("");
    setModalRecord(null);
  };

  // Initial fetch
  useEffect(() => {
    getRecords(sortOrder, orderByName, searchString, pageStart);
  }, [refetch, pageStart, searchString, query]);

  // Active Users Table Columns
  const activeUserColumn: TableColumnsType = [
    {
      title: "User Name",
      dataIndex: "userName",
      render: (value: any, record: any) => value || <Typography.Text disabled>-</Typography.Text>,
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "name"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "name"
            ? "descend"
            : undefined,
    },
    {
      title: "User Email Id",
      dataIndex: "emailAddress",
      render: (value: any) => value || <Typography.Text disabled>-</Typography.Text>,
    },
    {
      title: (
        <span>
          Work order_Approver
          <Popover
            overlayInnerStyle={{ maxWidth: 350, width: 350 }}
            placement="topLeft"
            content={
              <p>
                Work Order Approvers will have access to modules such as Dashboard, Work Order, and Work Order Approval.
                Their responsibility involves reviewing Work Order documents and approving Work Orders accordingly.
              </p>
            }
          >
            <Icon
              icon="bi:info-circle"
              style={{ color: "#ff4d4f", marginLeft: 5 }}
            />
          </Popover>
        </span>
      ),
      dataIndex: "roles",
      render: (value: any, record: any) => {
        const isReviewer = Array.isArray(record.roles) && record.roles.includes("PANEL_REVIEWER");
        return (
          <Switch
            checked={isReviewer}
            disabled
            onChange={(checked) => updateRole(record, "PANEL_REVIEWER", checked)}
            loading={switchLoading}
          />
        );
      },
    },
    {
      title: (
        <span>
          Work order_Admin
          <Popover
            overlayInnerStyle={{ maxWidth: 350, width: 350 }}
            placement="topLeft"
            content={
              <p>
                Work Order Admin will have access to all modules, including Dashboard, Work Order, Work Order Approval,
                Manage Catalogue, Approval Management, Work Order, User Management & Logs.
              </p>
            }
          >
            <Icon
              icon="bi:info-circle"
              style={{ color: "#ff4d4f", marginLeft: 5 }}
            />
          </Popover>
        </span>
      ),
      dataIndex: "roles",
      render: (value: any, record: any) => {
        const isAdmin = Array.isArray(record.roles) && record.roles.includes("PANEL_ADMIN");
        return (
          <Switch
            checked={isAdmin}
            disabled
            onChange={(checked) => updateRole(record, "PANEL_ADMIN", checked)}
            loading={switchLoading}
          />
        );
      },
    },
    /* Blank until the user is genuinely updated — the API stamps these at
       creation, so a newly added user looked as though they had been edited. */
    {
      title: "Updated At",
      dataIndex: "updatedOn",
      render: (value: any, record: any) =>
        wasUpdated(record) ? getFormattedDate(value) : <Typography.Text disabled>-</Typography.Text>,
    },
    {
      title: "Updated By",
      dataIndex: "updatedBy",
      render: (value: any, record: any) =>
        wasUpdated(record) && value ? value : <Typography.Text disabled>-</Typography.Text>,
    },
    {
      title: "Action",
      dataIndex: "action",
      render: (value: any, record: any) => (
        <div style={{ display: "flex", gap: 8 }}>
          {/* Deactivating yourself would lock you out, so it is not offered. */}
          {isSelf(record) ? (
            <Button
              title="You cannot deactivate your own account"
              danger
              disabled
            >
              <Icon
                fontSize={26}
                icon="mdi:user-block-outline"
              />
            </Button>
          ) : (
            <Popconfirm
              /* Wider than the 400px override.scss gives every popover, so
                 the note reads in a few lines — set inline, for this popover
                 only. The same file centres antd's icon against the whole
                 message, so that icon is switched off and drawn here, level
                 with the first line. */
              icon={null}
              overlayInnerStyle={{ width: 560 }}
              title={
                <div style={{ display: "flex", alignItems: "flex-start", gap: 10, lineHeight: 1.6 }}>
                  <Icon
                    icon="ant-design:exclamation-circle-filled"
                    style={{ color: "#faad14", fontSize: 16, marginTop: 3, flexShrink: 0 }}
                  />
                  <div>
                    <Typography.Text strong>
                      Are you sure you want to deactivate this user?
                    </Typography.Text>
                    <div style={{ marginTop: 6 }}>
                      <Typography.Text strong>Note:</Typography.Text> Deactivating this user will
                      revoke their access to the system module. The user will no longer be able to
                      create requisitions. Access can only be restored once the user is reactivated.
                    </div>
                  </div>
                </div>
              }
              placement="topLeft"
              onConfirm={() => updateActiveRole(record, false)}
              okText={"Yes"}
              cancelText={"Cancel"}
            >
              <Button
                title="Deactivate"
                danger={true}
              >
                <Icon
                  fontSize={26}
                  icon="mdi:user-block-outline"
                />
              </Button>
            </Popconfirm>
          )}
          <Button
            title={isSelf(record) ? "User cannot edit their roles themselves" : "Change Role"}
            disabled={isSelf(record)}
            onClick={() => handleChangeRole(record, value)}
          >
            <Icon
              fontSize={28}
              icon="fluent:person-swap-20-filled"
            />
          </Button>
        </div>
      ),
    },
  ];

  // Deactivated Users Table Columns
  const deactiveUserColumn: TableColumnsType = [
    {
      title: "User Name",
      dataIndex: "userName",
      render: (value: any, record: any) => value || <Typography.Text disabled>-</Typography.Text>,
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "name"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "name"
            ? "descend"
            : undefined,
    },
    {
      title: "User Email Id",
      dataIndex: "emailAddress",
      render: (value: any) => value || <Typography.Text disabled>-</Typography.Text>,
    },
    {
      title: (
        <span>
          Work order_Approver
          <Popover
            overlayInnerStyle={{ maxWidth: 350, width: 350 }}
            placement="topLeft"
            content={
              <p>
                Work Order Approvers will have access to modules such as Dashboard, Work Order, and Work Order Approval.
                Their responsibility involves reviewing Work Order documents and approving Work Orders accordingly.
              </p>
            }
          >
            <Icon
              icon="bi:info-circle"
              style={{ color: "#ff4d4f", marginLeft: 5 }}
            />
          </Popover>
        </span>
      ),
      dataIndex: "roles",
      render: (value: any, record: any) => {
        const isReviewer = Array.isArray(record.roles) && record.roles.includes("PANEL_REVIEWER");
        return (
          <Switch
            checked={isReviewer}
            disabled
            onChange={(checked) => updateRole(record, "PANEL_REVIEWER", checked)}
            loading={switchLoading}
          />
        );
      },
    },
    {
      title: (
        <span>
          Work order_Admin
          <Popover
            overlayInnerStyle={{ maxWidth: 350, width: 350 }}
            placement="topLeft"
            content={
              <p>
                Work Order Admin will have access to all modules, including Dashboard, Work Order, Work Order Approval,
                Manage Catalogue, Approval Management, Work Order, User Management & Logs.
              </p>
            }
          >
            <Icon
              icon="bi:info-circle"
              style={{ color: "#ff4d4f", marginLeft: 5 }}
            />
          </Popover>
        </span>
      ),
      dataIndex: "roles",
      render: (value: any, record: any) => {
        const isAdmin = Array.isArray(record.roles) && record.roles.includes("PANEL_ADMIN");
        return (
          <Switch
            checked={isAdmin}
            disabled
            onChange={(checked) => updateRole(record, "PANEL_ADMIN", checked)}
            loading={switchLoading}
          />
        );
      },
    },
    {
      title: "Updated At",
      dataIndex: "deactivatedOn",
      render: (value: any) => getFormattedDate(value) || <Typography.Text disabled>-</Typography.Text>,
    },
    {
      title: "Updated By",
      dataIndex: "deactivatedBy",
      render: (value: any) => value || <Typography.Text disabled>-</Typography.Text>,
    },
    {
      title: "Action",
      dataIndex: "action",
      render: (value: any, record: any) => (
        isSelf(record) ? (
          <Button
            disabled
            title="You cannot reactivate your own account"
          >
            Reactivate
          </Button>
        ) : (
          <Popconfirm
            title={"Are you sure to Reactivate the user"}
            okText={"Yes"}
            cancelText={"Cancel"}
            onConfirm={() => updateActiveRole(record, true)}
          >
            <Button>Reactivate</Button>
          </Popconfirm>
        )
      ),
    },
  ];

  // Tab items
  const items = [
    {
      label: "Active Users",
      key: "activeUsers",
      children: (
        <>
          <div ref={tableRef}>
            <DataTable
              columns={activeUserColumn?.map((column) => ({
                ...column,
                onHeaderCell: (column: any) => ({
                  onClick: () => {
                    if (column.sorter) {
                      handleTableSort(column, sortOrder, page ? page : 0);
                    }
                  },
                }),
              }))}
              tableData={
                data
                  ? data.map((d: any, index: any) => ({
                      ...d,
                      key: getKey(page, index),
                    }))
                  : null
              }
              title="Permissions"
              className="table-clickable"
              searchPlaceholderText={"Search User"}
              searchQuery={searchString}
              hideExport={false}
              exportFileName="Active_Users"
              exportApiUrl={apiUrl}
              inputDisable={isLoading}
              totalRecords={totalRecords}
              recordsPerPage={recordsPerPage}
              clearStringSearch={clearSearch}
              onSearchChange={handleSearchChange}
              multiSelect={true}
              isPagination={true}
              isServerSearchApi={true}
              excludeExportCols={["Updated At", "Updated By", "Action"]}
              includeExportCols={[
                {
                  displayName: "Current Role",
                  dataIndex: "roles",
                  render: (value: any) => {
                    return Array.isArray(value)
                      ? value.map((r: string) => ROLE_MAP[r] || r).join(", ")
                      : ROLE_MAP[value] || value || "-";
                  },
                },
                {
                  displayName: "Updated At",
                  dataIndex: "updatedOn",
                  render: (value: any) => getFormattedDate(value) || "-",
                },
                {
                  displayName: "Updated By",
                  dataIndex: "updatedBy",
                  render: (value: any) => value,
                },
              ]}
              tabOffset={60}
            />
          </div>
          <div className="pagination">
            <Pagination
              defaultCurrent={1}
              current={page + 1}
              total={totalRecords}
              onChange={(newPage) => {
                const startIndex = newPage - 1;
                setPage(newPage - 1);
                setPageStart(startIndex);
                getRecords(sortOrder, orderByName, searchString, startIndex);
              }}
              showSizeChanger={false}
            />
          </div>
        </>
      ),
    },
    {
      label: "Deactivated Users",
      key: "deactivatedUsers",
      children: (
        <>
          <div ref={tableRef}>
            <DataTable
              columns={deactiveUserColumn?.map((column: any) => ({
                ...column,
                onHeaderCell: (column: any) => ({
                  onClick: () => {
                    if (column.sorter) {
                      handleTableSort(column, sortOrder, page ? page : 0);
                    }
                  },
                }),
              }))}
              tableData={
                data
                  ? data.map((d: any, index: any) => ({
                      ...d,
                      key: getKey(page, index),
                    }))
                  : null
              }
              title="Permissions"
              className="table-clickable"
              searchPlaceholderText={"Search User"}
              searchQuery={searchString}
              exportFileName="Deactivated_Users"
              hideExport={false}
              exportApiUrl={apiUrl}
              totalRecords={totalRecords}
              onSearchChange={handleSearchChange}
              recordsPerPage={recordsPerPage}
              clearStringSearch={clearSearch}
              multiSelect={true}
              isPagination={true}
              isServerSearchApi={true}
              excludeExportCols={["Deactivated At", "Deactivated By", "Action"]}
              includeExportCols={[
                {
                  displayName: "Current Role",
                  dataIndex: "roles",
                  render: (value: any) => {
                    return Array.isArray(value)
                      ? value.map((r: string) => ROLE_MAP[r] || r).join(", ")
                      : ROLE_MAP[value] || value || "-";
                  },
                },
                {
                  displayName: "Deactivated At",
                  dataIndex: "deactivatedOn",
                  render: (value: any) => getFormattedDate(value) || "-",
                },
                {
                  displayName: "Deactivated By",
                  dataIndex: "deactivatedBy",
                  render: (value: any) => value,
                },
              ]}
              tabOffset={60}
            />
          </div>
          <div className="pagination">
            <Pagination
              defaultCurrent={1}
              current={page + 1}
              total={totalRecords}
              onChange={(newPage) => {
                const startIndex = newPage - 1;
                setPage(newPage - 1);
                setPageStart(startIndex);
                getRecords(sortOrder, orderByName, searchString, startIndex);
              }}
              showSizeChanger={false}
            />
          </div>
        </>
      ),
    },
  ];

  return (
    <ContentWrapper
      title={"Work Order User Management"}
      actionItems={
        <Button
          type="primary"
          onClick={() => navigate(`/workorder-user-management/add`)}
        >
          <Space>
            <Icon
              icon="akar-icons:plus"
              inline
            />
            Add
          </Space>
        </Button>
      }
    >
      {(buttonLoading || switchLoading || isLoading) && (
        <div className="loadingOverlay">
          <Space size="middle">
            <Spin size="large" />
          </Space>
        </div>
      )}

      <div className={pillStyles.pillTabs}>
        {/* Unmounted when left, so each table's ticked rows go with it —
            they used to be waiting on return (see ApprovalManagement). */}
        <Tabs
          destroyInactiveTabPane
          activeKey={query?.get("activeTab") || ""}
          items={items}
          onChange={(activeKey) => {
            /* Each tab opens on its default list: no search, page 1, default sort. */
            setSearchString("");
            setClearSearch(true);
            setPageStart(0);
            setPage(0);
            setSortOrder("desc");
            setOrderByName("createdOn");
            navigate(`/workorder-user-management?activeTab=${activeKey}`);
          }}
        />
      </div>

      {/* Rejection Modal */}
      <Formik
        onSubmit={(values, formikHelpers) => {
          setCommentLoading(true);
          const payload = {
            userId: rejectRecord?.id,
            rejectionReason: values.rejectionReason,
          };
          api
            .post(`${panelApiUrl}/user/reject`, payload)
            .then((res: any) => {
              setCommentLoading(false);
              setModal(false);
              setRefetch((prevState: boolean) => !prevState);
            })
            .catch((err: any) => {
              setCommentLoading(false);
              message.error("Error rejecting user");
            });
        }}
        initialValues={initialValue}
        validationSchema={validationSchema}
      >
        {({ submitForm, resetForm, setFieldValue, touched, errors, isValid, dirty, values }) => (
          <Modal
            title={"Rejection Reason"}
            maskClosable={false}
            open={modal}
            onOk={() => {
              submitForm();
            }}
            okButtonProps={{ disabled: !(isValid && dirty) }}
            onCancel={() => {
              setModal(false);
              resetForm();
            }}
            confirmLoading={commentLoading}
            okText={"Reject"}
            width={600}
            centered
          >
            <Form
              layout="vertical"
              onFinish={submitForm}
            >
              <Field name={"rejectionReason"}>
                {({ field, meta }: FieldProps) => {
                  return (
                    <Form.Item required
                      label={"Please comment rejection reason"}
                      validateStatus={meta.touched && meta.error ? "error" : ""}
                    >
                      <TextArea
                        rows={4}
                        maxLength={5000}
                        showCount
                        {...field}
                      />
                      {meta.touched && meta.error && (
                        <Typography.Text type="danger">{meta.error.toString()}</Typography.Text>
                      )}
                    </Form.Item>
                  );
                }}
              </Field>
            </Form>
          </Modal>
        )}
      </Formik>

      {/* Division Modal */}
      <Formik
        initialValues={defaultValue}
        onSubmit={(values, formikHelpers) => {
          setLoading(true);
          const roles = [];
          for (const [key, value] of Object.entries(values)) {
            if (value && isBoolean(value)) {
              roles.push(key);
            }
          }
          if (roles.length === 0) {
            message.info("At least a single role must be selected");
            setLoading(false);
            return;
          }

          const payload = {
            userId: selectedRecord?.id,
            newRoles: roles,
          };

          api
            .put(`${panelApiUrl}/user/update-division-roles`, payload)
            .then((res) => {
              setSuccessLoader(true);
              setLoading(false);
              setRefetch((prevState) => !prevState);
              formikHelpers.resetForm();
              setDivisionModal(false);
            })
            .catch((err) => {
              setLoading(false);
              setDivisionModal(false);
              formikHelpers.resetForm();
              message.error("Error updating user roles");
            });
        }}
        validationSchema={validationSchema1}
      >
        {(props) => {
          return (
            <Modal
              title={
                <Typography.Title
                  level={4}
                  style={{ marginBottom: 10 }}
                >
                  User Role Change
                </Typography.Title>
              }
              open={divisionModal}
              width={500}
              onCancel={() => {
                props.resetForm();
                setDivisionModal(false);
              }}
              okText={"Submit"}
              footer={null}
            >
              <Form
                layout="vertical"
                onFinish={props.handleSubmit}
              >
                <Row style={{ display: "block", width: "100%" }}>
                  <Col
                    xs={24}
                    md={16}
                  >
                    <Row gutter={16}>
                      <Col
                        md={24}
                        sm={24}
                      >
                        <Form.Item label={"Select Roles"}>
                          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                            <Checkbox
                              name="Approver"
                              checked={props.values.Approver}
                              onChange={(e) => props.setFieldValue("Approver", e.target.checked)}
                            >
                              Approver
                            </Checkbox>
                            <Checkbox
                              name="Admin"
                              checked={props.values.Admin}
                              onChange={(e) => props.setFieldValue("Admin", e.target.checked)}
                            >
                              Admin
                            </Checkbox>
                          </div>
                        </Form.Item>
                      </Col>
                    </Row>
                  </Col>
                </Row>
                <Row>
                  <Col
                    xs={16}
                    md={24}
                    sm={24}
                    style={{ textAlign: "right" }}
                  >
                    <Space>
                      <Button
                        htmlType="submit"
                        type="primary"
                        loading={loading}
                      >
                        Submit
                      </Button>
                    </Space>
                  </Col>
                </Row>
              </Form>
            </Modal>
          );
        }}
      </Formik>

      {/* Change Role Modal */}
      <Modal
        open={!!selectedRowId}
        onCancel={handleModalClose}
        footer={null}
      >
        <Formik<RoleFormValues>
          initialValues={{ role: modalValue ? (Array.isArray(modalValue) ? modalValue : [modalValue]) : [] }}
          enableReinitialize
          onSubmit={(values) => {
            setLoading(true);
            /* No access picked means Work Order Creator. An empty list does not
               do it: the API strips every role and answers "User deleted…". */
            updateRole(modalRecord, values.role.length ? values.role : ["PANEL_CREATOR"], true);
          }}
          validationSchema={validationSchemaModal}
        >
          {(props) => {
            return (
              <Form
                layout="vertical"
                onFinish={props.handleSubmit}
              >
                <Row style={{ marginTop: 16, display: "block", width: "100%" }}>
                  <Col xs={24}>
                    <Row gutter={16}>
                      <Col>
                        <Typography.Title
                          level={5}
                          style={{ marginBottom: 10 }}
                        >
                          Choose the access you want
                        </Typography.Title>
                      </Col>
                      <Col>
                        <Form.Item name="role">
                          <Space
                            direction="vertical"
                            size={12}
                            style={{ width: "100%" }}
                          >
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                width: "50%",
                              }}
                            >
                              <Typography.Text>Work Order Approver</Typography.Text>
                              <Switch
                                checked={props.values.role.includes("PANEL_REVIEWER")}
                                /* One access or the other, never both: switching one on switches the other off. */
                                onChange={(checked) => props.setFieldValue("role", checked ? ["PANEL_REVIEWER"] : [])}
                              />
                            </div>

                            {/* Work Order Admin */}
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                width: "50%",
                              }}
                            >
                              <Typography.Text>Work Order Admin</Typography.Text>
                              <Switch
                                checked={props.values.role.includes("PANEL_ADMIN")}
                                /* One access or the other, never both: switching one on switches the other off. */
                                onChange={(checked) => props.setFieldValue("role", checked ? ["PANEL_ADMIN"] : [])}
                              />
                            </div>

                            <Alert
                              type="info"
                              showIcon={false}
                              message={
                                <Typography.Text>
                                  <strong>Note:</strong> If none of the roles are selected, the system will
                                  automatically assign the user the Work Order Creator role.
                                </Typography.Text>
                              }
                              style={{
                                background: "#f7f5ff",
                                border: "none",
                              }}
                            />
                          </Space>
                        </Form.Item>
                        <Typography.Text type="danger">
                          <ErrorMessage name="role" />
                        </Typography.Text>
                      </Col>
                    </Row>
                  </Col>
                </Row>
                <div style={{ textAlign: "right" }}>
                  <Space>
                    <Button
                      htmlType="submit"
                      type="primary"
                      loading={loading}
                    >
                      Submit
                    </Button>
                  </Space>
                </div>
              </Form>
            );
          }}
        </Formik>
        <SuccessModal
          redirect={-1}
          title="Successfully Created User"
          message="You have successfully created a new user"
          type="success"
          buttonText="Go Back"
          open={successLoader}
          setOpen={setSuccessLoader}
        />
      </Modal>

      {/* Success Modal */}
      <SuccessModal
        redirect={"/workorder-user-management"}
        title={"Successfully Updated"}
        message={"User roles have been successfully updated"}
        type={"success"}
        buttonText={"Go Back"}
        open={successLoader}
        setOpen={setSuccessLoader}
      />
    </ContentWrapper>
  );
};
